import styled from '@emotion/styled';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import { PREDICTED_RANK_VIEWED } from '@constants/segment';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import useRankPredictorSegmentAnalytics from '../Common/useRankPredictorSegmentAnalytics';
import { getExamInfo, getExamUIDByGoalUID } from '../utils';
import { StyledButton } from './Form';

const Title = styled(H2)`
  margin-bottom: ${Spacings.SPACING_10B};
`;

const Card = styled.div`
  position: relative;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_10B};
`;

const Disclaimer = styled(P1)`
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const StyledImage = styled.img`
  height: ${Spacings.SPACING_25B};
  width: ${Spacings.SPACING_25B};
  top: 0px;
  right: 0px;
  position: absolute;
  border-radius: ${Spacings.SPACING_2B};
`;

const Divider = styled.hr`
  margin: ${Spacings.SPACING_6B} 0px;
  border-top: 1px solid var(--color-divider);
`;

const ScoreText = styled(P1)`
  color: var(--color-text-secondary);
  width: 80%;
`;

const RankInfo = styled.div`
  font-size: ${Fonts.H2};
  font-weight: ${Fonts.BOLD};
  margin-top: ${Spacings.SPACING_3B};
`;

const PrecentileInfo = styled.div`
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.SEMIBOLD};
`;

const ButtonWrapper = styled.div`
  display: flex;
`;

const Result = ({
  score,
  rank,
  percentile,
  onPredictAgain,
  OnStartLeaning,
  isUnacademyPredictor = false,
  isOfflineRankPredictor = false
}) => {
  const { query } = useRouter();
  const { goalUID } = query;

  const examUID = getExamUIDByGoalUID(goalUID);
  const examInfo = getExamInfo(examUID);
  const rankText = `${rank.min} - ${rank.max}`;
  const percentileText = `Estimated percentile : ${percentile.min} - ${percentile.max}`;
  const segmentAnalytics = useRankPredictorSegmentAnalytics();
  useEffect(() => {
    if (rank.min && rank.max) {
      const eventsAnalytics = {};
      if (isUnacademyPredictor || isOfflineRankPredictor)
        eventsAnalytics.is_offline = true;
      segmentAnalytics(PREDICTED_RANK_VIEWED, eventsAnalytics);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rank]);

  return (
    <>
      <Title>Your predicted rank</Title>
      <Card>
        <StyledImage
          src={getImage('rankpredictor/rank.svg')}
          height={60}
          width={60}
        />
        <ScoreText>{`For a score of ${score}, your rank might be`}</ScoreText>
        <RankInfo>{rankText}</RankInfo>
        <Divider />
        <PrecentileInfo>{percentileText}</PrecentileInfo>
      </Card>
      <Disclaimer>
        Disclaimer: The estimated rank is based on the expert analysis of the
        exam difficulty level based on date and slot
      </Disclaimer>
      <>
        {examInfo.hasStartLearningCTA ? (
          <ButtonWrapper>
            <StyledButton
              label="Start learning"
              onClick={OnStartLeaning}
              $colorType={examInfo.buttonColor}
            />
            <StyledButton
              label="Predict again"
              onClick={onPredictAgain}
              variant="hollow"
              color="black"
            />
          </ButtonWrapper>
        ) : (
          <StyledButton label="Predict again" onClick={onPredictAgain} />
        )}
      </>
    </>
  );
};

export default Result;
